import React from "react";
import "./styles.scss";
import Container from "../../componentes/Container";
import { useForm, Controller } from "react-hook-form";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { buscarAgendamentoEmAndamento } from "./services/buscarEmAndamento";
import { ApiServico, buscarServicos } from "./services/buscarServico";
import { finalizarAtendimento } from "./services/finalizarAtendimento";
import {
  cancelarAgendamento,
  CancelarAgendamentoPayload,
} from "./services/cancelarAgendamento";
import { format, isValid, parse, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";

type FormValues = {
  status: "sim" | "nao";
  opcoes: string[];
  justificativa?: string;
};

function parseDateSafe(input?: string | Date | null): Date | null {
  if (!input) return null;

  if (input instanceof Date) return isValid(input) ? input : null;

  try {
    const br = parse(input as string, "dd/MM/yyyy HH:mm", new Date());
    if (isValid(br)) return br;
  } catch {}

  try {
    const iso = parseISO(input as string);
    if (isValid(iso)) return iso;
  } catch {}

  try {
    const d = new Date(input as string);
    if (isValid(d)) return d;
  } catch {}

  return null;
}

const AtendimentoEmAndamento: React.FC = () => {
  const { control, handleSubmit, setValue, watch } = useForm<FormValues>({
    defaultValues: { status: "sim", opcoes: [], justificativa: "" },
  });

  const queryClient = useQueryClient();

  const {
    data: atendimento,
    isLoading: loadingAtendimento,
    isError: errorAtendimento,
  } = useQuery({
    queryKey: ["agendamento", "em-andamento"],
    queryFn: buscarAgendamentoEmAndamento,
  });

  const {
    data: servicos,
    isLoading: loadingServicos,
    isError: errorServicos,
  } = useQuery<ApiServico[]>({
    queryKey: ["servicos"],
    queryFn: buscarServicos,
  });

  const { mutateAsync: finalizar, isPending: finalizando } = useMutation({
    mutationFn: finalizarAtendimento,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["agendamento", "em-andamento"],
      });
    },
    onError: (err: any) => {
      const resp = err?.response?.data;
      alert(
        resp?.mensagens?.join("\n") ||
          resp?.mensagemDaExcecao ||
          err?.message ||
          "Erro ao finalizar atendimento."
      );
    },
  });

  const { mutateAsync: cancelar, isPending: cancelando } = useMutation({
    mutationFn: (payload: CancelarAgendamentoPayload) =>
      cancelarAgendamento(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["agendamento", "em-andamento"],
      });
      setValue("justificativa", "");
    },
    onError: (err: any) => {
      const resp = err?.response?.data;
      alert(
        resp?.mensagens?.join("\n") ||
          resp?.mensagemDaExcecao ||
          err?.message ||
          "Erro ao cancelar agendamento."
      );
    },
  });

  const opcoesSelecionadas = watch("opcoes");
  const status = watch("status");
  const justificativa = watch("justificativa");

  // -------------------------
  // Regras para habilitar o botão
  // -------------------------
  const justificativaTrim = (justificativa ?? "").trim();
  const precisaServicos = status === "sim";
  const precisaJustificativa = status === "nao";

  const temServicoSelecionado =
    precisaServicos && (opcoesSelecionadas?.length ?? 0) > 0;
  const justificativaPreenchida =
    precisaJustificativa && justificativaTrim.length > 0;

  const podeEnviar = precisaServicos
    ? temServicoSelecionado
    : precisaJustificativa
    ? justificativaPreenchida
    : false;

  const toggleOpcao = (valor: string) => {
    const atual = [...(opcoesSelecionadas ?? [])];
    if (atual.includes(valor)) {
      setValue(
        "opcoes",
        atual.filter((v) => v !== valor)
      );
    } else {
      setValue("opcoes", [...atual, valor]);
    }
  };

  const onSubmit = async (data: FormValues) => {
    if (!atendimento) return;

    if (data.status === "nao") {
      const motivo = (data.justificativa || "").trim();
      if (!motivo) {
        alert("Informe o motivo do não atendimento.");
        return;
      }
      await cancelar({ agendamentoId: atendimento.id, justificativa: motivo });
      return;
    }

    const idsSelecionados =
      (servicos ?? [])
        .filter((s) => data.opcoes.includes(s.nome))
        .map((s) => s.id) ?? [];

    if (idsSelecionados.length === 0) {
      alert("Selecione ao menos um serviço.");
      return;
    }

    await finalizar({
      agendamentoId: atendimento.id,
      servicoIds: idsSelecionados,
    });
  };

  const renderBlocoAtendimento = () => {
    if (loadingAtendimento) {
      return (
        <div className="box-atendimento loading">
          <div className="skeleton skeleton-time" />
          <div className="skeleton skeleton-lines" />
        </div>
      );
    }
    if (errorAtendimento) {
      return (
        <div className="box-atendimento erro">
          <p>Não foi possível carregar o atendimento em andamento.</p>
        </div>
      );
    }
    if (!atendimento) {
      return (
        <div className="empty-atendimento" role="status" aria-live="polite">
          <div className="empty-icon-wrapper">
            <svg viewBox="0 0 24 24" fill="none" className="empty-icon">
              <path
                d="M8 2v3M16 2v3M4 9h16M6 6h12a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2Z"
                strokeWidth="1.6"
                stroke="currentColor"
              />
            </svg>
          </div>

          <h3>Nenhum atendimento em andamento</h3>
          <p>
            Você pode iniciar um novo atendimento na aba{" "}
            <strong>“Todos os atendimentos”</strong>.
          </p>
        </div>
      );
    }

    const inicio = parseDateSafe(atendimento.dataInicial);
    const hora = inicio ? format(inicio, "HH:mm", { locale: ptBR }) : "--:--";
    const dataFmt = inicio ? format(inicio, "dd, MMMM", { locale: ptBR }) : "-";

    const telefone = atendimento.pessoa?.telefoneCelular || "-";

    return (
      <div className="box-atendimento">
        <div className="horario">
          <strong>{hora}</strong>
          <span>{dataFmt}</span>
        </div>
        <div className="info">
          <p>
            <strong>Nome</strong>
            <br />
            {atendimento.pessoa?.nome ?? "-"}
          </p>
          <p>
            <strong>CPF</strong>
            <br />
            {atendimento.pessoa?.cpf ?? "-"}
          </p>
          <p>
            <strong>Telefone</strong>
            <br />
            {telefone}
          </p>
        </div>
      </div>
    );
  };

  const renderServicos = () => {
    if (loadingServicos) {
      return (
        <div className="checkbox-grupos">
          <div className="skeleton skeleton-group" />
          <div className="skeleton skeleton-group" />
          <div className="skeleton skeleton-group" />
        </div>
      );
    }
    if (errorServicos) {
      return (
        <div className="checkbox-grupos erro">
          <p>Não foi possível carregar a listagem de serviços.</p>
        </div>
      );
    }
    if (!servicos || servicos.length === 0) {
      return (
        <div className="checkbox-grupos vazio">
          <p>Nenhum serviço disponível.</p>
        </div>
      );
    }

    return (
      <div className="checkbox-grupos">
        <div>
          <strong>Serviços</strong>
          {servicos.map((s) => (
            <label key={s.id}>
              <input
                type="checkbox"
                checked={opcoesSelecionadas.includes(s.nome)}
                onChange={() => toggleOpcao(s.nome)}
              />{" "}
              {s.nome}
            </label>
          ))}
        </div>
      </div>
    );
  };

  const temAtendimento = !!atendimento;

  return (
    <Container comFundo tamanhoDoContainer="grande">
      <div className="pagina-atendimento">
        <h2>Em andamento</h2>
        <p>
          Cada servidor pode atender uma pessoa por vez. Abaixo você pode ver o
          atendimento em andamento.
        </p>

        {renderBlocoAtendimento()}

        {temAtendimento && (
          <form className="form-atendimento" onSubmit={handleSubmit(onSubmit)}>
            <h3>Como foi o atendimento?</h3>

            <div className="grupo">
              <p>O atendimento aconteceu?</p>
              <Controller
                control={control}
                name="status"
                render={({ field }) => (
                  <>
                    <label>
                      <input
                        type="radio"
                        value="sim"
                        checked={field.value === "sim"}
                        onChange={field.onChange}
                      />
                      Sim
                    </label>
                    <label>
                      <input
                        type="radio"
                        value="nao"
                        checked={field.value === "nao"}
                        onChange={field.onChange}
                      />
                      Não
                    </label>
                  </>
                )}
              />
            </div>

            {status === "nao" && (
              <div className="grupo">
                <label htmlFor="justificativa">Motivo do não atendimento</label>
                <Controller
                  control={control}
                  name="justificativa"
                  render={({ field }) => (
                    <textarea
                      id="justificativa"
                      placeholder="Descreva brevemente o motivo..."
                      value={field.value || ""}
                      onChange={field.onChange}
                      rows={4}
                      className="textarea-justificativa"
                    />
                  )}
                />
                <small
                  className={`hint ${justificativaPreenchida ? "" : "erro"}`}
                >
                  Este campo é obrigatório para cancelar.
                </small>
              </div>
            )}

            {status === "sim" && (
              <>
                {renderServicos()}
                {opcoesSelecionadas.length === 0 && (
                  <small className="hint erro">
                    Selecione ao menos um serviço para concluir.
                  </small>
                )}
              </>
            )}

            <button
              type="submit"
              className="botao-finalizar"
              disabled={finalizando || cancelando || !podeEnviar}
            >
              {status === "nao"
                ? cancelando
                  ? "Cancelando..."
                  : "Cancelar agendamento"
                : finalizando
                ? "Concluindo..."
                : "Concluir atendimento"}
            </button>
          </form>
        )}
      </div>
    </Container>
  );
};

export default AtendimentoEmAndamento;
